<template>
  <div v-if="review">
    <section id="section-top-customer-reviews">
      <SectionHeading
        id="toc-top-customer-reviews"
        class="heading"
        icon="icon-multiple-users-1"
        title="All trip reviews"
      />

      <div class="review-container">
        <div class="review">
          <div class="reviewer-info">
            <div class="reviewer-name">{{ review.guestName }}</div>
            <div class="review-date">{{ formatDate(review.submittedAt) }}</div>
          </div>
          <CustomerReviewStarRating :rating="review.score" />
        </div>

        <a v-if="review.title && review.url" :href="review.url" class="trip-link">
          {{ review.title }}
        </a>

        <div v-if="review.stoodOut">{{ review.stoodOut }}</div>
      </div>

      <div id="see-more-review" @click="showMore" class="see-all-btn">See more trip reviews</div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useItineraryStore } from '@/stores/itinerary.js'
import SectionHeading from '@/modules/Itinerary/Components/SectionHeading.vue'
import { CustomerReview } from '@/types'
import moment from 'moment'
import { scrollToElement } from '@/helpers/documentHelpers.js'
import CustomerReviewStarRating from '@/modules/Itinerary/Components/CustomerReviewStarRating.vue'

const store = useItineraryStore()

const review = computed<CustomerReview | null>(() => {
  return store.reviews.length > 0 ? store.reviews[0] : null
})

const showMore = () => scrollToElement('#toc-customer-reviews', 75)

const formatDate = dateString => {
  if (!dateString) return ''
  return moment(dateString).fromNow()
}

const getStarFillPercentage = (starPosition, rating) => {
  if (starPosition <= Math.floor(rating)) {
    return '100%' // Full star
  } else if (starPosition === Math.ceil(rating) && rating % 1 !== 0) {
    return `${(rating % 1) * 100}%` // Partial star
  }
  return '0%' // Empty star
}
</script>

<style lang="scss" scoped>
#section-top-customer-reviews {
  @include flex-column($spacing-3);
}

.review-container {
  @include flex-column($spacing-3);
  background: $grey-25;
  padding: $spacing-6;
  border-radius: $rounded-xl;
}

.review {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: $spacing-1;

  .avatar {
    width: $spacing-12;
    height: $spacing-12;
    border-radius: $rounded-max;
    object-fit: cover;
    background: $grey-25;
  }

  .reviewer-info {
    flex: 1;
    @include flex-column;

    .reviewer-name {
      font-weight: $font-semibold;
    }

    .review-date {
      font-size: $text-sm;
      color: $grey-100;
    }
  }
}

.trip-link {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: $grey-300;
  text-decoration: none;
  padding: 12px;
  background: #d4d4d433;
  border-radius: 8px;
  transition: background 100ms ease;

  &:hover {
    background: $green-50;
  }

  .icon {
    font-size: 12px;
  }
}

.see-all-btn {
  align-self: center;
  color: $brand-tertiary;

  &:hover {
    cursor: pointer;
  }
}
</style>
